import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ShoppingCart, User, Search, Menu, X } from 'lucide-react';

const Header25 = ({ cartCount = 0 }) => {
    const navigate = useNavigate();
    const [scrolled, setScrolled] = useState(false);
    const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

    useEffect(() => {
        const handleScroll = () => {
            setScrolled(window.scrollY > 50);
        };
        window.addEventListener('scroll', handleScroll);
        return () => window.removeEventListener('scroll', handleScroll);
    }, []);

    useEffect(() => {
        if (mobileMenuOpen) {
            document.body.style.overflow = 'hidden';
        } else {
            document.body.style.overflow = '';
        }
        return () => {
            document.body.style.overflow = '';
        };
    }, [mobileMenuOpen]);

    const navLinks = [
        { label: 'Home', path: '/template25' },
        { label: 'Kurtas', path: '/template25/shop' },
        { label: 'Sherwanis', path: '/template25/shop' },
        { label: 'Lehengas', path: '/template25/shop' },
        { label: 'About', path: '/template25/about' },
        { label: 'Contact', path: '/template25/contact' },
        { label: 'Orders', path: '/template25/orders' },
    ];

    const handleNavClick = (path) => {
        navigate(path);
        setMobileMenuOpen(false);
    };

    return (
        <>
            <style>{`
                @import url('https://fonts.googleapis.com/css2?family=Space+Grotesk:wght@300;400;500;600;700&family=IBM+Plex+Sans:wght@300;400;500;600;700&display=swap');
            `}</style>

            <header
                style={{
                    position: 'fixed',
                    top: 0,
                    left: 0,
                    right: 0,
                    zIndex: 50,
                    backgroundColor: scrolled ? '#FFFFFF' : '#FFFFFF',
                    borderBottom: scrolled ? '1px solid #DEE2E6' : '1px solid transparent',
                    transition: 'all 0.3s ease',
                    boxShadow: scrolled ? '0 1px 3px rgba(0,0,0,0.05)' : 'none',
                }}
            >
                <div
                    style={{
                        maxWidth: '1400px',
                        margin: '0 auto',
                        padding: '0 24px',
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        height: '72px',
                    }}
                >
                    {/* Left: Brand */}
                    <h1
                        onClick={() => navigate('/template25')}
                        style={{
                            fontFamily: "'Space Grotesk', sans-serif",
                            fontSize: '24px',
                            fontWeight: 700,
                            color: '#111111',
                            cursor: 'pointer',
                            letterSpacing: '3px',
                            margin: 0,
                            textTransform: 'uppercase',
                        }}
                    >
                        SAHAJ
                    </h1>

                    {/* Center: Nav Links (Desktop) */}
                    <nav
                        style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '32px',
                        }}
                        className="sahaj-desktop-nav"
                    >
                        {navLinks.map((link) => (
                            <button
                                key={link.label}
                                onClick={() => handleNavClick(link.path)}
                                style={{
                                    fontFamily: "'IBM Plex Sans', -apple-system, sans-serif",
                                    fontSize: '13px',
                                    fontWeight: 500,
                                    color: '#111111',
                                    background: 'none',
                                    border: 'none',
                                    cursor: 'pointer',
                                    padding: 0,
                                    letterSpacing: '0.5px',
                                    textTransform: 'uppercase',
                                    transition: 'color 0.3s ease',
                                }}
                                onMouseEnter={(e) => (e.target.style.color = '#0D7C66')}
                                onMouseLeave={(e) => (e.target.style.color = '#111111')}
                            >
                                {link.label}
                            </button>
                        ))}
                    </nav>

                    {/* Right: Icons */}
                    <div style={{ display: 'flex', alignItems: 'center', gap: '18px' }}>
                        <Search
                            size={20}
                            style={{ color: '#111111', cursor: 'pointer', transition: 'color 0.3s ease' }}
                            onMouseEnter={(e) => (e.currentTarget.style.color = '#0D7C66')}
                            onMouseLeave={(e) => (e.currentTarget.style.color = '#111111')}
                        />
                        <User
                            size={20}
                            style={{ color: '#111111', cursor: 'pointer', transition: 'color 0.3s ease' }}
                            onClick={() => navigate('/template25/profile')}
                            onMouseEnter={(e) => (e.currentTarget.style.color = '#0D7C66')}
                            onMouseLeave={(e) => (e.currentTarget.style.color = '#111111')}
                        />
                        <div
                            style={{ position: 'relative', cursor: 'pointer' }}
                            onClick={() => navigate('/template25/cart')}
                        >
                            <ShoppingCart
                                size={20}
                                style={{ color: '#111111', transition: 'color 0.3s ease' }}
                                onMouseEnter={(e) => (e.currentTarget.style.color = '#0D7C66')}
                                onMouseLeave={(e) => (e.currentTarget.style.color = '#111111')}
                            />
                            {cartCount > 0 && (
                                <span
                                    style={{
                                        position: 'absolute',
                                        top: '-8px',
                                        right: '-8px',
                                        backgroundColor: '#0D7C66',
                                        color: '#FFFFFF',
                                        fontSize: '10px',
                                        fontWeight: 700,
                                        fontFamily: "'IBM Plex Sans', -apple-system, sans-serif",
                                        width: '18px',
                                        height: '18px',
                                        borderRadius: 0,
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                    }}
                                >
                                    {cartCount}
                                </span>
                            )}
                        </div>

                        {/* Mobile Hamburger */}
                        <button
                            className="sahaj-mobile-menu-btn"
                            onClick={() => setMobileMenuOpen(true)}
                            style={{
                                display: 'none',
                                background: 'none',
                                border: 'none',
                                cursor: 'pointer',
                                padding: 0,
                            }}
                        >
                            <Menu size={24} style={{ color: '#111111' }} />
                        </button>
                    </div>
                </div>
            </header>

            {/* Mobile Full-Screen Overlay */}
            <div
                style={{
                    position: 'fixed',
                    inset: 0,
                    zIndex: 100,
                    backgroundColor: '#FFFFFF',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    opacity: mobileMenuOpen ? 1 : 0,
                    pointerEvents: mobileMenuOpen ? 'auto' : 'none',
                    transition: 'opacity 0.3s ease',
                }}
            >
                <button
                    onClick={() => setMobileMenuOpen(false)}
                    style={{
                        position: 'absolute',
                        top: '24px',
                        right: '24px',
                        background: 'none',
                        border: 'none',
                        cursor: 'pointer',
                        padding: 0,
                    }}
                >
                    <X size={28} style={{ color: '#111111' }} />
                </button>

                {/* Brand in mobile menu */}
                <h2
                    style={{
                        fontFamily: "'Space Grotesk', sans-serif",
                        fontSize: '20px',
                        fontWeight: 700,
                        color: '#0D7C66',
                        letterSpacing: '3px',
                        marginBottom: '40px',
                        textTransform: 'uppercase',
                    }}
                >
                    SAHAJ
                </h2>

                <nav style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '28px' }}>
                    {navLinks.map((link) => (
                        <button
                            key={link.label}
                            onClick={() => handleNavClick(link.path)}
                            style={{
                                fontFamily: "'Space Grotesk', sans-serif",
                                fontSize: '22px',
                                fontWeight: 600,
                                color: '#111111',
                                background: 'none',
                                border: 'none',
                                cursor: 'pointer',
                                padding: 0,
                                letterSpacing: '2px',
                                textTransform: 'uppercase',
                                transition: 'color 0.3s ease',
                            }}
                            onMouseEnter={(e) => (e.target.style.color = '#0D7C66')}
                            onMouseLeave={(e) => (e.target.style.color = '#111111')}
                        >
                            {link.label}
                        </button>
                    ))}
                </nav>
            </div>

            {/* Responsive Styles */}
            <style>{`
                .sahaj-desktop-nav {
                    display: flex !important;
                }
                .sahaj-mobile-menu-btn {
                    display: none !important;
                }
                @media (max-width: 768px) {
                    .sahaj-desktop-nav {
                        display: none !important;
                    }
                    .sahaj-mobile-menu-btn {
                        display: block !important;
                    }
                }
            `}</style>
        </>
    );
};

export default Header25;
